<template>
  <div v-if="currentArticle" class="d-flex flex-column align-center pb-12">
    <v-sheet color="#c4be9c" rounded class="width-100 pa-6 rounded-30">
      
      <div class="d-flex flex-row position-relative width-100">
        <!-- Left Arrow -->
        <div class="d-none d-md-flex flex-column flex-md-row align-center px-2">
          <v-btn
            v-if="articles.length > 1"
            @click="previousArticle"
            icon
            color="#b35438"
            large
            aria-label="Previous local lore article"
          >
            <v-icon>mdi-chevron-left</v-icon>
          </v-btn>
        </div>

        <!-- Content -->
        <div class="d-flex flex-grow-1 flex-column align-center pa-0 pa-md-6">
          <div class="d-flex jost-bold grey--text text--darken-3 text-uppercase justify-center mb-8 font-size-xl">Local Lore</div>
          <div class="d-flex flex-column flex-md-row align-center">
            
            <!-- Image on Left -->
            <div class="flex-1">
              <v-img
                v-if="currentArticle.coverImage"
                :src="$img(currentArticle.coverImage, 1200)"
                class="cursor-pointer local-lore-photo"
                :alt="currentArticle.title"
                @click="openArticle(currentArticle)"
              ></v-img>
            </div>

            <!-- Content on Right -->
            <div class="flex-grow-1 pl-0 pl-md-12 pt-4 pt-md-0">
              <h2 class="barlow-condensed-regular renedian-dark-red--text text-uppercase mb-3 mt-6 mt-md-0 font-size-lg line-height-4 font-weight-bold">
                {{ currentArticle.title }}
              </h2>

              <!-- Excerpt/Body Text -->
              <div class="mb-4 grey--text text--darken-3 font-size-sm line-height-3">
                {{ currentArticle.excerpt || getFirstParagraph(currentArticle) }}
              </div>

              <!-- Read More Link -->
              <div class="mb-4">
                <span @click="openArticle(currentArticle)" class="cursor-pointer barlow-condensed-regular text-uppercase text-decoration-underline font-size-sm" style="font-weight: 600; letter-spacing: 0.05em; transition: opacity 0.2s ease;" @mouseover="$event.target.style.opacity = '0.7'" @mouseleave="$event.target.style.opacity = '1'">
                  READ MORE...
                </span>
              </div>

              <!-- Read More Local Lore Button -->
              <div class="d-flex justify-end">
                <v-btn
                  @click="goToLorePage"
                  class="barlow-condensed-regular text-uppercase"
                  text
                  outlined
                  :style="{ borderStyle: 'dashed', borderColor: '#711c0c', color: '#711c0c', borderRadius: '8px', transition: 'background-color 0.2s ease, color 0.2s ease' }"
                  @mouseover="$event.target.style.backgroundColor = 'rgba(179, 84, 56, 0.1)'"
                  @mouseleave="$event.target.style.backgroundColor = 'transparent'"
                >
                  READ MORE LOCAL LORE
                </v-btn>
              </div>  
            </div>

          </div>
        </div>

        <!-- Right Arrow -->
        <div class="d-none d-md-flex flex-column flex-md-row align-center px-2">
          <v-btn
            v-if="articles.length > 1"
            @click="nextArticle"
            icon
            color="#b35438"
            large
            aria-label="Next local lore article"
          >
            <v-icon>mdi-chevron-right</v-icon>
          </v-btn>
        </div>

      </div>

    </v-sheet>
  </div>
</template>
<script>
import { getFirestore, collection, getDocs, query, where } from 'firebase/firestore'

export default {
  name: 'LocalLore',
  props: {
    region: { type: String, required: true },
  },
  data: () => ({
    articles: [],
    currentIndex: 0,
  }),
  computed: {
    currentArticle() {
      return this.articles.length > 0 ? this.articles[this.currentIndex] : null
    }
  },
  async created() {
    await this.loadArticles()
  },
  methods: {
    async loadArticles() {
      try {
        const db = getFirestore()
        const articlesRef = collection(db, 'articles')
        
        // Fetch all published articles
        const q = query(
          articlesRef,
          where('published', '==', true)
        )
        const snap = await getDocs(q)
        
        // Filter articles by type, section, and region
        const matchingArticles = []
        snap.forEach(doc => {
          const data = doc.data()
          const type = (data.type || '').toLowerCase()
          const section = (data.section || '').toLowerCase()
          
          // Check type and section
          if (type === 'resources' && section === 'local-lore') {
            // Check if region matches
            const articleRegion = data.region
            let regionName = null
            
            if (articleRegion) {
              // Region can be an object with a name property, or just a string
              if (typeof articleRegion === 'object' && articleRegion.name) {
                regionName = articleRegion.name
              } else if (typeof articleRegion === 'string') {
                regionName = articleRegion
              }
            }
            
            // Match region (case-insensitive)
            if (regionName && regionName.toLowerCase() === this.region.toLowerCase()) {
              matchingArticles.push({
                id: doc.id,
                ...data,
              })
            }
          }
        })
        
        // Store all matching articles and start at a random index
        if (matchingArticles.length > 0) {
          this.articles = matchingArticles
          this.currentIndex = Math.floor(Math.random() * matchingArticles.length)
        } else {
          this.articles = []
          this.currentIndex = 0
        }
      } catch (error) {
        console.error('Failed to load local lore articles', error)
        this.articles = []
        this.currentIndex = 0
      }
    },
    previousArticle() {
      if (this.articles.length > 0) {
        this.currentIndex = (this.currentIndex - 1 + this.articles.length) % this.articles.length
      }
    },
    nextArticle() {
      if (this.articles.length > 0) {
        this.currentIndex = (this.currentIndex + 1) % this.articles.length
      }
    },
    getFirstParagraph(article) {
      // Extract first paragraph from blocks or html
      if (article.html) {
        const match = article.html.match(/<p[^>]*>(.*?)<\/p>/i)
        if (match) {
          const text = match[1].replace(/<[^>]*>/g, '')
          return text.length > 300 ? text.substring(0, 300) + '...' : text
        }
      }
      if (Array.isArray(article.blocks)) {
        const textBlock = article.blocks.find(b => b.type === 'text' && b.text)
        if (textBlock) {
          const text = textBlock.text.replace(/<[^>]*>/g, '')
          return text.length > 300 ? text.substring(0, 300) + '...' : text
        }
      }
      return ''
    },
    openArticle(article) {
      const p = article && article.path
      if (typeof p === 'string' && p.startsWith('/')) {
        this.go(p) // use global mixin navigation to avoid duplicate issues
      }
    },
    goToLorePage() {
      this.go('/resources/local-lore')
    }
  }
}
</script>
<style scoped>
  .rounded-30 {
    border-radius: 30px !important;
  }

  .local-lore-photo {
    border: 10px solid white;
    border-radius: 10px;
    box-shadow: 0 10px 24px rgba(0,0,0,0.18), 0 6px 12px rgba(0,0,0,0.12);
    transform: rotate(-4deg);
    object-fit: cover;
    width: 300px
  }

  @media (min-width: 500px) {
    .local-lore-photo {
      width: 400px;
      border-width: 12px;
      border-radius: 12px;
    }
  }

  @media (min-width: 1200px) {
    .local-lore-photo {
      transform: rotate(-2deg);
      width: 500px;
      border-width: 14px;
      border-radius: 14px;
    }
  }

  @media (max-width: 959px) {
    h2 {
      font-size: 1.75rem !important;
    }
  }
</style>